import { useToast } from '@app/core/notifications';
import { AuditLiveMentionConfidence } from '@mi-front/core';
import { useCallback } from 'react';
import { defineMessage, useIntl } from 'react-intl';

const REJECT_CONFIRM_MESSAGE = defineMessage({
  id: 'audit.liveMentions.rejectConfirm',
  defaultMessage:
    '¿Confirma rechazar mention? En tal caso, se eliminará y se reprocesará en búsqueda de mejores resultados.',
});

const REJECTED_MESSAGE = defineMessage({
  id: 'audit.liveMentions.rejected',
  defaultMessage: 'Rechazada',
});

export interface UseMentionConfidenceActionsParams {
  confidence: AuditLiveMentionConfidence;
  onConfidenceChange: (confidence: AuditLiveMentionConfidence) => void;
}

export interface UseMentionConfidenceActionsResult {
  onAccept: () => void;
  onReject: () => void;
  canChange: boolean;
}

/**
 * Accept toggles ACCEPTED ↔ AUTO. Reject asks for confirmation, then sets REJECTED.
 */
export function useMentionConfidenceActions({
  confidence,
  onConfidenceChange,
}: UseMentionConfidenceActionsParams): UseMentionConfidenceActionsResult {
  const { $t } = useIntl();
  const { notifyInfo } = useToast();
  const canChange = confidence !== AuditLiveMentionConfidence.REJECTED;

  const onAccept = useCallback(() => {
    onConfidenceChange(
      confidence === AuditLiveMentionConfidence.ACCEPTED
        ? AuditLiveMentionConfidence.AUTO
        : AuditLiveMentionConfidence.ACCEPTED,
    );
  }, [confidence, onConfidenceChange]);

  const onReject = useCallback(() => {
    if (!window.confirm($t(REJECT_CONFIRM_MESSAGE))) {
      return;
    }

    onConfidenceChange(AuditLiveMentionConfidence.REJECTED);
    notifyInfo($t(REJECTED_MESSAGE));
  }, [$t, notifyInfo, onConfidenceChange]);

  return { onAccept, onReject, canChange };
}
